/**
 * Client-side HubSpot analytics — the one place that talks to `window._hsq`.
 *
 * All any of this does is push items onto `window._hsq`, a plain queue array
 * that HubSpot's tracking loader (injected via GTM) drains whenever it finishes
 * loading — which might be immediately, after a delay, or never (ad blocker, no
 * consent, tag absent). Pushing before/without the loader is a harmless no-op,
 * so we get graceful degradation for free.
 *
 * The point of stitching is attribution: linking the current browser's
 * `hubspotutk` cookie to a known email lets HubSpot back-associate prior
 * anonymous activity (page views, original source, first touch) onto the
 * contact — whether it already exists or is created later via our CRM API. This
 * is purely additive analytics: it never reads `hubspotutk`, never sends
 * anything to our backend, and does not touch the CRM contact-write path.
 *
 * Every messy detail (is tracking on? is the queue there? already stitched this
 * email? has the queue drained?) lives behind this facade, written once.
 */

type HubSpotQueue = Array<[string, ...unknown[]]>;

const TRUTHY = new Set(["true", "1", "yes", "on"]);

// Accept the values people actually type, in one place — so staging/preview
// behave the way QA expects instead of silently no-opping unless it is exactly
// "true".
function isEnabled(): boolean {
  if (process.env.NODE_ENV === "production") return true;
  const flag =
    process.env.NEXT_PUBLIC_HUBSPOT_TRACKING_ENABLED?.toLowerCase() ?? "";
  return TRUTHY.has(flag);
}

// Is the queue there? Null on the server or before `_hsq` exists.
function getQueue(): HubSpotQueue | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { _hsq?: HubSpotQueue };
  return (w._hsq ??= []);
}

function normalize(email?: string | null): string | null {
  const e = email?.trim().toLowerCase();
  return e || null;
}

/**
 * Fire on a discrete conversion moment (lead capture / signup) where the email
 * is first known. Safe to call unconditionally from an event handler.
 *
 * The trailing `trackPageView` is REQUIRED: `identify` only stages the identity
 * and does not commit until it is followed by a tracked event.
 *
 * Returns whether it actually queued, so callers (e.g. `identifyOnce`) can tell
 * a real success from a no-op.
 */
function identify(email?: string | null): boolean {
  const emailN = normalize(email);
  const hsq = getQueue();
  if (!isEnabled() || !emailN || !hsq) return false;
  hsq.push(["identify", { email: emailN }]);
  hsq.push(["trackPageView"]);
  return true;
}

/**
 * Fire once per browser session per email (authenticated session start / first
 * authenticated pageview). Guards against re-firing on every client navigation,
 * which would otherwise inflate HubSpot page-view counts.
 */
function identifyOnce(email?: string | null): void {
  const emailN = normalize(email);
  if (!emailN) return;
  const key = `hs_identified:${emailN}`;

  try {
    if (sessionStorage.getItem(key)) return;
  } catch {
    // sessionStorage unavailable (private mode / disabled) — fall through and
    // identify anyway; a duplicate pageview beats skipping the stitch.
  }

  // Only remember it if it ACTUALLY happened. A blocked queue now retries on
  // the next navigation instead of being silenced for the whole session.
  if (identify(emailN)) {
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      // ignore
    }
  }
}

/**
 * Let the queue empty before a hard navigation tears it down. Resolves when
 * `_hsq` drains or after `timeoutMs`, whichever comes first — so the
 * highest-intent event (a signup) actually lands before we leave the page.
 */
function flush(timeoutMs = 500): Promise<void> {
  const hsq = getQueue();
  if (!hsq || hsq.length === 0) return Promise.resolve();
  return new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      if (hsq.length === 0 || performance.now() - start > timeoutMs) resolve();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

export const hubspotTracker = { identify, identifyOnce, flush };
